import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { format, startOfWeek, addWeeks } from "date-fns";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  apiGetTimekeepingEmployees, apiGetTimesheetWeek, apiGetTimekeepingTasks, apiAdminSaveTimesheet,
  apiGetTimekeepingSettings,
  type TimesheetWeek, type TimesheetEntryInput,
} from "@/lib/api";
import { WeekFlow } from "@/components/timekeeping/WeekFlow";
import { getTimekeepingErrorMessage } from "@/lib/timekeeping-errors";

const toISO = (d: Date) => format(startOfWeek(d, { weekStartsOn: 1 }), "yyyy-MM-dd");

export function AdminTimecardEntry() {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const [employeeId, setEmployeeId] = useState<string>("");
  const [anchor, setAnchor] = useState<Date>(new Date());
  const weekISO = toISO(anchor);

  const { data: employees = [] } = useQuery({
    queryKey: ["timekeeping-admin-employees"],
    queryFn: apiGetTimekeepingEmployees,
  });
  const { data: tasks = [] } = useQuery({
    queryKey: ["timekeeping-tasks"],
    queryFn: () => apiGetTimekeepingTasks(false),
  });
  const { data: settings } = useQuery({
    queryKey: ["timekeeping-settings"],
    queryFn: apiGetTimekeepingSettings,
  });
  const { data: week, isLoading } = useQuery<TimesheetWeek>({
    queryKey: ["admin-timesheet-week", employeeId, weekISO],
    queryFn: () => apiGetTimesheetWeek(weekISO, Number(employeeId)),
    enabled: !!employeeId,
  });

  const saveMut = useMutation({
    mutationFn: (entries: TimesheetEntryInput[]) => apiAdminSaveTimesheet(Number(employeeId), weekISO, entries),
    onSuccess: (updated) => {
      qc.setQueryData(["admin-timesheet-week", employeeId, weekISO], updated);
      toast.success(t("timekeeping.admin.entrySaved", { defaultValue: "Timecard saved" }));
    },
    onError: (e) => toast.error(getTimekeepingErrorMessage(e, t)),
  });

  return (
    <div className="space-y-5">
      <p className="text-sm text-muted-foreground">
        {t("timekeeping.admin.entryHint", {
          defaultValue: "Enter or correct an employee's weekly timecard. This works for closed weeks too — the week's status is left unchanged.",
        })}
      </p>

      <Select value={employeeId} onValueChange={setEmployeeId}>
        <SelectTrigger className="h-9 w-full sm:w-72">
          <SelectValue placeholder={t("timekeeping.admin.selectEmployee", { defaultValue: "Select employee…" })} />
        </SelectTrigger>
        <SelectContent>
          {employees.map((e) => (
            <SelectItem key={e.id} value={String(e.id)}>{e.name}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      {!employeeId ? (
        <div className="text-center text-sm text-muted-foreground py-10 border rounded-lg">
          {t("timekeeping.admin.selectEmployeePrompt", { defaultValue: "Select an employee to enter their timecard." })}
        </div>
      ) : isLoading || !week ? (
        <Skeleton className="h-96 w-full rounded-2xl" />
      ) : (
        <WeekFlow
          week={week}
          tasks={tasks}
          editable
          saving={saveMut.isPending}
          submitting={false}
          maxDailyHours={settings?.maxDailyHours}
          allowFutureDates={settings?.allowFutureDates}
          showSubmit={false}
          showCopy={false}
          onPrevWeek={() => setAnchor(addWeeks(anchor, -1))}
          onNextWeek={() => setAnchor(addWeeks(anchor, 1))}
          onThisWeek={() => setAnchor(new Date())}
          onSaveDraft={(entries) => saveMut.mutate(entries)}
        />
      )}
    </div>
  );
}
